import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { Growl } from 'angular/custom/services/growl.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';
import * as angular from 'angular';
import { TogglePanelCallback } from 'angular/custom/components/toggle-panel.component';

class OperatorSettingsFcfsLeadTimesComponentCtrl implements ng.IController {
    public leadTimesEnabled: boolean = false;
    public settings: TsoFcfsSettingsDto;
    public tsoRefId: number;
    public newLeadTimeRule: LeadTimeRuleDto;

    public numberOfNetworkPointsWithOverrides: number = -1;

    private onLastRuleDeleted: Function;

    constructor(
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private growl: Growl,
        private errorHandler: ErrorHandler
    ) {}

    $onInit() {
        this.TransmissionSystemOperator.getNumberOfNetworkPointsWithSpecificLeadTimes(
            { id: this.tsoRefId },
            (res: number) => {
                this.numberOfNetworkPointsWithOverrides = res;
                this.leadTimesEnabled =
                    this.settings.leadTimeRules.length > 0 ||
                    this.numberOfNetworkPointsWithOverrides > 0;
            }
        );
    }

    public saveLeadTimeNote(): ng.IPromise<any> {
        return this.TransmissionSystemOperator.saveFcfsLeadTimeDescription(
            { id: this.tsoRefId },
            { value: this.settings.leadTimeDescription }
        ).$promise;
    }

    public startAddingNewLeadTimeRule(contractPeriodType: ContractPeriodType) {
        let name =
            contractPeriodType === 'NON_WITHINDAY'
                ? ''
                : contractPeriodType === 'FULL_WITHINDAY'
                  ? '1st within-day'
                  : 'Subsequent within-day';
        this.newLeadTimeRule = <LeadTimeRuleDto>{
            name: name,
            mode: 'FCFS',
            contractPeriodType: contractPeriodType,
            contractPeriod: null,
            leadTimePeriodForMinimumTimeBefore: {
                value: null,
                type: 'DAY'
            },
            leadTimePeriodForMaximumTimeAhead: {
                value: null,
                type: 'DAY'
            }
        };
    }

    public deleteLeadTime(leadTimeId: number) {
        this.TransmissionSystemOperator.deleteFcfsLeadTimeRule(
            { id: this.tsoRefId, leadTimeId: leadTimeId },
            () => {
                this.growl.show(
                    'The lead time was successfully deleted',
                    'success'
                );
                this.settings.leadTimeRules.splice(
                    this.settings.leadTimeRules.findIndex(
                        (leadTimeRule) => leadTimeRule.id === leadTimeId
                    ),
                    1
                );
                this.TransmissionSystemOperator.getNumberOfNetworkPointsWithSpecificLeadTimes(
                    { id: this.tsoRefId },
                    (res: number) => {
                        this.numberOfNetworkPointsWithOverrides = res;
                        if (
                            this.settings.leadTimeRules.length === 0 &&
                            this.numberOfNetworkPointsWithOverrides === 0
                        ) {
                            this.leadTimesEnabled = false;
                            this.settings.leadTimeDescription = '';
                            this.saveLeadTimeNote();
                        }
                    }
                );
            },
            this.errorHandler
        );
    }

    public resetNewLeadTimeRule() {
        delete this.newLeadTimeRule;
    }

    public toggleLeadTimesEnabled(cb: TogglePanelCallback) {
        return this.TransmissionSystemOperator.disableFcfsLeadTimeRule(
            { id: this.tsoRefId },
            () => {
                this.settings.leadTimeRules = [];
                this.settings.leadTimeDescription = '';
                this.TransmissionSystemOperator.getNumberOfNetworkPointsWithSpecificLeadTimes(
                    { id: this.tsoRefId },
                    (res: number) =>
                        (this.numberOfNetworkPointsWithOverrides = res)
                );
                cb(true);
            },
            () => cb(false)
        );
    }

    public get leadTimeRules() {
        const contractPeriodTypeSortOrder: ContractPeriodType[] = [
            'SUBSEQUENT_WITHINDAY',
            'FULL_WITHINDAY',
            'NON_WITHINDAY'
        ];
        return this.settings.leadTimeRules.sort((ruleA, ruleB) => {
            if (ruleA.contractPeriodType !== ruleB.contractPeriodType) {
                return (
                    contractPeriodTypeSortOrder.indexOf(
                        ruleA.contractPeriodType
                    ) -
                    contractPeriodTypeSortOrder.indexOf(
                        ruleB.contractPeriodType
                    )
                );
            } else {
                if (
                    ruleA.contractPeriod == null &&
                    ruleB.contractPeriod == null
                ) {
                    return 0; // this should only happen if there is a duplicate rule...
                } else if (ruleA.contractPeriod == null) {
                    return 1;
                } else if (ruleB.contractPeriod == null) {
                    return -1;
                } else {
                    return ruleA.contractPeriod - ruleB.contractPeriod;
                }
            }
        });
    }

    public get fullWithinDayLeadTimeRule() {
        return this.settings.leadTimeRules.filter(
            (rule) => rule.contractPeriodType === 'FULL_WITHINDAY'
        )[0];
    }

    public get subsequentWithinDayLeadTimeRule() {
        return this.settings.leadTimeRules.filter(
            (rule) => rule.contractPeriodType === 'SUBSEQUENT_WITHINDAY'
        )[0];
    }

    public getContractPeriodString(leadTimeRule: LeadTimeRuleDto) {
        if (leadTimeRule.contractPeriodType === 'SUBSEQUENT_WITHINDAY') {
            return '< 1 day';
        } else if (leadTimeRule.contractPeriodType === 'FULL_WITHINDAY') {
            return '1 day';
        } else {
            return leadTimeRule.contractPeriod
                ? '< ' +
                      leadTimeRule.contractPeriod +
                      ' month' +
                      (leadTimeRule.contractPeriod > 1 ? 's' : '')
                : 'Open end rule';
        }
    }
}

angular.module('app').component('pkpOperatorSettingsFcfsLeadTimes', {
    controller: OperatorSettingsFcfsLeadTimesComponentCtrl,
    template:
        require('app/operator-settings/operator-settings-fcfs-lead-times.component.html'),
    bindings: {
        settings: '=',
        tsoRefId: '<'
    }
});
